import { RevoluteJoint, type Body } from "fast-2d";
import { Camera } from "../render/Camera";
import type { Demo, Scene } from "./types";
import { makeWorld, floor, box, ball } from "./helpers";

/**
 * A chain of links (revolute joints) hanging from a fixed point with a heavy
 * ball on the end. Released horizontally, it swings down under gravity and
 * smashes a stack of crates — momentum transfer through a joint chain.
 */
export const wreckingBall: Demo = {
	id: "wrecking-ball",
	title: "Wrecking ball",
	blurb:
		"A heavy ball on a jointed chain, released horizontally so gravity swings it straight through a stack of crates. Click to launch another.",
	category: "Featured",
	build(): Scene {
		const world = makeWorld();
		floor(world, 60);

		const links = 9;
		const linkW = 0.55;
		const linkH = 0.16;
		const anchorX = -6.5;
		const anchorY = -7;

		const buildChain = (): void => {
			const chain: Body[] = [];
			for (let i = 0; i < links; i++) {
				// Lay the chain out horizontally so it falls and swings.
				const cx = anchorX + linkW / 2 + i * linkW;
				chain.push(box(world, cx, anchorY, linkW, linkH, { density: 1 }));
			}
			// Pin the first link to a fixed world point.
			world.addConstraint(
				new RevoluteJoint(chain[0]!, null, {
					localAnchorA: [-linkW / 2, 0],
					localAnchorB: [anchorX, anchorY],
				}),
			);
			// Hinge the links together.
			for (let i = 0; i < links - 1; i++) {
				world.addConstraint(
					new RevoluteJoint(chain[i]!, chain[i + 1]!, {
						localAnchorA: [linkW / 2, 0],
						localAnchorB: [-linkW / 2, 0],
					}),
				);
			}
			// Heavy ball pinned to the last link.
			const last = chain[links - 1]!;
			const heavy = ball(world, anchorX + links * linkW + 0.8, anchorY, 0.8, {
				density: 16,
				friction: 0.6,
			});
			world.addConstraint(
				new RevoluteJoint(last, heavy, { localAnchorA: [linkW / 2, 0], localAnchorB: [-0.8, 0] }),
			);
		};

		buildChain();

		// A pyramid of crates sitting on the measured swing path. The ball starts to
		// the right of the pivot and sweeps down-left; placing the stack a little to
		// the right of the bottom of the arc lets it hit the pyramid face directly.
		const crate = 0.9;
		const stackX = anchorX + 2.1;
		for (let row = 0; row < 5; row++) {
			const n = 5 - row;
			for (let c = 0; c < n; c++) {
				const x = (c - (n - 1) / 2) * (crate + 0.02) + stackX;
				const yy = -crate / 2 - row * crate;
				box(world, x, yy, crate, crate, { density: 1, friction: 0.6 });
			}
		}

		return {
			world,
			camera: new Camera(-3, -3.5, 22),
			hint: "Click to fling a ball",
			onPointerDown(x, y): void {
				ball(world, x, y, 0.5, { density: 14, friction: 0.5, velocity: [6, 2] });
			},
		};
	},
};
